// Live tuning panel: sliders for every registered param, grouped, persisted
// locally. Only shown with ?dev in the URL.

import { allParams, resetParams, setParam, type Param } from '../config/params';

export interface PanelAction {
  label: string;
  run: () => void;
}

export class DevPanel {
  private readonly root: HTMLElement;
  private readonly body: HTMLElement;
  private readonly inputs = new Map<string, { range: HTMLInputElement; out: HTMLElement }>();

  constructor(container: HTMLElement, actions: PanelAction[]) {
    this.root = document.createElement('div');
    this.root.className = 'dev-panel collapsed';
    this.root.innerHTML = `<button class="dev-toggle">⚙︎ tune</button><div class="dev-body"></div>`;
    container.appendChild(this.root);
    this.body = this.root.querySelector('.dev-body') as HTMLElement;
    this.root.querySelector('.dev-toggle')!.addEventListener('click', () => this.root.classList.toggle('collapsed'));
    // Keep touches on the panel from reaching the pedals underneath.
    this.root.addEventListener('pointerdown', (e) => e.stopPropagation());

    const bar = document.createElement('div');
    bar.className = 'dev-actions';
    for (const a of [...actions, { label: 'Reset all', run: () => this.resetAll() }]) {
      const b = document.createElement('button');
      b.textContent = a.label;
      b.addEventListener('click', a.run);
      bar.appendChild(b);
    }
    this.body.appendChild(bar);

    const groups = new Map<string, Param[]>();
    for (const p of allParams()) {
      const list = groups.get(p.group) ?? [];
      list.push(p);
      groups.set(p.group, list);
    }
    for (const [group, params] of groups) {
      const details = document.createElement('details');
      details.innerHTML = `<summary>${group}</summary>`;
      for (const p of params) details.appendChild(this.row(p));
      this.body.appendChild(details);
    }
  }

  private row(p: Param): HTMLElement {
    const row = document.createElement('label');
    row.className = 'dev-row';
    const name = document.createElement('span');
    name.textContent = p.label;
    const out = document.createElement('output');
    const range = document.createElement('input');
    range.type = 'range';
    range.min = String(p.min);
    range.max = String(p.max);
    range.step = String(p.step);
    range.value = String(p.value);
    out.textContent = fmt(p.value);
    out.classList.toggle('changed', p.value !== p.defaultValue);
    range.addEventListener('input', () => {
      const v = Number(range.value);
      setParam(p.id, v);
      out.textContent = fmt(v);
      out.classList.toggle('changed', v !== p.defaultValue);
    });
    row.append(name, out, range);
    this.inputs.set(p.id, { range, out });
    return row;
  }

  private resetAll(): void {
    resetParams();
    for (const p of allParams()) {
      const ui = this.inputs.get(p.id);
      if (!ui) continue;
      ui.range.value = String(p.value);
      ui.out.textContent = fmt(p.value);
      ui.out.classList.remove('changed');
    }
  }
}

function fmt(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(Math.abs(v) < 1 ? 3 : 2);
}
